import React, { FC, Suspense, useCallback, useState } from 'react';
import {
  Alert,
  Box,
  ButtonAppearance,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSize,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import { Block, CreateBlockError, CreateBlockInput, CreateBlockPayload } from '@bechtel/types';
import { useMutation } from '@apollo/client';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import { flatten, groupBy, map, reduce } from 'lodash';
import { array, object, string } from 'yup';
import {
  useBlockPermission,
  TitleValue,
  ErrorBoundary,
  SuspenseLoader,
  SetFile,
  DrawerFooterButtonStyled,
} from '@bechtel/client-common';
import { CREATE_BLOCK } from '@bechtel/client-gql';
import { CreateSingleBlock as SingleBlock } from './single/internals';
import { CreateMultiBlock as MultiBlock } from './multi/internals';
import { BlockNotCreated } from './blocks-not-created';
import { t, Trans } from '@lingui/macro';
import { CommodityList, CreateBlockProps, FormikState } from './types';

const blockValidationSchema = object().shape({
  comments: string()
    .min(2, t`Too Short!`)
    .max(2000, t`Too Long!`)
    .required(t`Comment is mandatory`),
  blockTypeId: string().required(t`Block type is mandatory`),
  milestoneIds: array()
    .nullable()
    .notRequired()
    .when('showMilestone', {
      is: (value) => value == false,
      then: array()
        .required()
        .min(1, t`Select at least 1 milestone`)
        .nullable(),
    }),
  mediaMetadata: array()
    .required()
    .max(3, t`Max 3 photos can be uploaded`),
});

export const CreateBlock: FC<CreateBlockProps> = ({
  workPackageId,
  workPackageTag,
  assignedCrewMembers,
  weldCommodityAssignees,
  facilities,
  multiBlock,
  close,
  commodityTypes,
  refetchQueries,
  selectedMilestone,
  commodities = [],
}) => {
  const dispatchToast = useToastsDispatch();
  let setFilesFunc: SetFile | undefined = undefined;
  const [submittedBlocks, setSubmittedBlocks] = useState<Block[]>([]);
  const [submittedBlockErrors, setSubmittedBlockErrors] = useState<CreateBlockError[]>([]);
  const [commoditiesSelected, setCommoditiesSelected] = useState<boolean>(false);

  const initialValues: FormikState = {
    comments: '',
    blockTypeId: '',
    milestoneIds: [],
    mediaMetadata: [],
    commodityType: '',
    commodityList: [],
    showMilestone: !!commodities[0]?.currentMilestoneType?.id,
  };

  const openCreateBlockToast = (blockCount: number) => {
    if (blockCount > 0) {
      const createBlockToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${blockCount} ${blockCount === 1 ? t`Block` : t`Blocks(s)`} ${t`Successfully Submitted`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(createBlockToast);
    }
  };

  const {
    visible: blockNotCreatedModalVisible,
    open: openBlockNotCreatedModal,
    close: closeBlockNotCreatedModal,
  } = useModalState(false);

  const closeSidesheet = () => {
    if (multiBlock) {
      openCreateBlockToast(submittedBlocks && submittedBlocks.length);
    }
    if (close) {
      close();
    }
  };

  const closeBlockErrorModal = () => {
    closeBlockNotCreatedModal();
    if (multiBlock) {
      setSubmittedBlockErrors([]);
    }
  };

  // NOTE: this is used to track the successfully submitted block mutations
  const onBlockSubmitComplete = useCallback(
    (data: { createBlock: CreateBlockPayload }) => {
      if (multiBlock) {
        if (data.createBlock.block) {
          setSubmittedBlocks((prev: Block[]) => {
            return [...prev, data.createBlock.block as Block];
          });
        }
      }
    },
    [multiBlock]
  );

  const [createBlock, { loading }] = useMutation<{ createBlock: CreateBlockPayload }, { input: CreateBlockInput }>(
    CREATE_BLOCK,
    { onCompleted: onBlockSubmitComplete, refetchQueries: refetchQueries }
  );

  const { canCreateBlock, ...rest } = useBlockPermission(
    assignedCrewMembers,
    facilities ? facilities : [],
    null,
    weldCommodityAssignees
  );

  if (!canCreateBlock) {
    return (
      <Alert>
        <Trans>Sorry, you do not have rights to create block</Trans>
      </Alert>
    );
  }

  const blockErrors = groupBy(submittedBlockErrors, (blockError) => {
    return (blockError.milestone?.commodity.materialCommodity || blockError.milestone?.commodity.weld)?.id;
  });

  const onSetFiles = (setFiles: SetFile) => {
    setFilesFunc = setFiles;
  };
  const weldCurrentMilestone = commodities[0]?.milestones?.find((milestone) => {
    return milestone?.type?.id === commodities[0]?.currentMilestoneType?.id;
  });

  return (
    <ErrorBoundary>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {blockNotCreatedModalVisible && <BlockNotCreated blockErrors={blockErrors} onClose={closeBlockErrorModal} />}
      </Suspense>
      <Formik
        initialValues={
          selectedMilestone
            ? { ...initialValues, milestoneIds: [[{ id: selectedMilestone.id, name: selectedMilestone.name }]] }
            : initialValues
        }
        validateOnBlur={false}
        validationSchema={blockValidationSchema}
        onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
          // NOTE: this brings our milestoneIds signature from
          // [[obj, obj, obj], [obj, obj, obj]] to [id, id, id, id, id]
          const flattenedMilestoneIds = map(flatten(values.milestoneIds), 'id');

          const input = {
            blockTypeId: values.blockTypeId,
            comments: values.comments,
            attachmentMetadata: values.mediaMetadata,
            milestoneIds: commodities[0]?.currentMilestoneType?.id ? weldCurrentMilestone?.id : flattenedMilestoneIds,
          };

          const { data: createBlockData } = await createBlock({
            variables: { input },

            // update: (cache, result) => {
            //   if (get(result, 'data.createBlock.block')) {
            //     clearBlocksCache(cache);
            //   }
            // },
          });
          const createBlockError = createBlockData?.createBlock.error;
          if (createBlockError && createBlockError.length > 0) {
            setSubmittedBlockErrors(createBlockData?.createBlock.error as CreateBlockError[]);
            openBlockNotCreatedModal();
          } else {
            if (multiBlock) {
              actions.resetForm({
                values: {
                  ...initialValues,
                  commodityList: values.commodityList,
                  commodityType: values.commodityType,
                },
              });
            } else {
              actions.resetForm();
              if (createBlockData?.createBlock?.block) {
                openCreateBlockToast(1);
                if (createBlockError && createBlockError.length === 0) closeSidesheet();
              }
            }
          }
          if (setFilesFunc) setFilesFunc([]);
        }}
      >
        {(props: FormikProps<FormikState>) => {
          // NOTE: For multi commodity blocking we need to collect all milestone ids as those
          //will be used as the unique identifier for a particular commodity
          let flattenedMilestones;
          if (selectedMilestone) flattenedMilestones = [selectedMilestone];
          else
            flattenedMilestones = multiBlock
              ? reduce<CommodityList, CommodityList['milestones']>(
                  props.values.commodityList,
                  (prev, curr) => {
                    return [...prev, ...curr.milestones];
                  },
                  []
                )
              : // @ts-ignore typescript issue in getting value from groupcommodities
              commodities[0]?.currentMilestoneType?.id
              ? [weldCurrentMilestone]
              : commodities[0].milestones;

          const groupedMilestones = groupBy(flattenedMilestones, 'name');

          return (
            <SideSheet
              onDismiss={closeSidesheet}
              size={multiBlock ? SideSheetSize.MEDIUM : SideSheetSize.DEFAULT}
              visible
            >
              <SideSheetHeader title={multiBlock ? t`Multi Block` : t`Block`}>
                <SideSheetClose onClick={closeSidesheet}></SideSheetClose>
              </SideSheetHeader>
              <SideSheetSubHeader>
                <TitleValue data-testid="wp-tag" title={t`Work Package`} value={workPackageTag}></TitleValue>

                {!multiBlock && (
                  <Box>
                    {/* @ts-ignore typescript issue in getting value from groupcommodities */}
                    <TitleValue
                      data-testid="commodity-tag"
                      title={t`Commodity`}
                      // @ts-ignore typescript issue in getting value from groupcommodities
                      value={commodities[0].tag}
                    ></TitleValue>
                  </Box>
                )}
                {commodities[0]?.currentMilestoneType?.id && (
                  <TitleValue title={t`Milestone`} value={commodities[0]?.currentMilestoneType?.name} />
                )}
              </SideSheetSubHeader>
              <SideSheetBody>
                <ErrorBoundary>
                  <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                    {multiBlock && (
                      <MultiBlock
                        workPackageId={workPackageId}
                        onSetFiles={onSetFiles}
                        milestones={groupedMilestones}
                        commodityTypes={commodityTypes}
                        commoditiesSelected={commoditiesSelected}
                        selectedCommodities={props.values.commodityList}
                        submittedBlocks={submittedBlocks}
                        selectedCommodityTypeId={props.values.commodityType}
                      />
                    )}
                    {!multiBlock && (
                      <SingleBlock
                        onSetFiles={onSetFiles}
                        milestones={groupedMilestones}
                        selectedMilestone={selectedMilestone}
                        showMilestonesList={!!commodities[0]?.currentMilestoneType?.id}
                      />
                    )}
                  </Suspense>
                </ErrorBoundary>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    <DrawerFooterButtonStyled
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={closeSidesheet}
                      disabled={loading}
                    >
                      {multiBlock && submittedBlocks.length > 0 ? t`Done` : t`Cancel`}
                    </DrawerFooterButtonStyled>
                  </Box>
                  {((multiBlock && commoditiesSelected) || !multiBlock) && (
                    <Box>
                      <DrawerFooterButtonStyled
                        data-testid="submit"
                        onClick={() => {
                          props.handleSubmit();
                        }}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={!props.dirty || !props.isValid || loading}
                      >
                        {loading && <Spinner />}
                        {!loading && <Trans>Submit Block</Trans>}
                      </DrawerFooterButtonStyled>
                    </Box>
                  )}
                  {multiBlock && !commoditiesSelected && (
                    <Box>
                      <DrawerFooterButtonStyled
                        disabled={props.values.commodityList.length === 0}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={() => {
                          setCommoditiesSelected(true);
                        }}
                      >
                        <Trans>Next</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  )}
                </Box>
              </SideSheetFooter>
            </SideSheet>
          );
        }}
      </Formik>
    </ErrorBoundary>
  );
};
